import { useEffect, useRef, useState } from 'react';
import { ChevronRight, Home, Loader, FileText, Search, User, LogOut, RefreshCw } from 'lucide-react';
import FileRow, { type FileRowProps } from './FileRow';
import FolderRow from './FolderRow';

interface AccountInfo {
    name?: string;
    email?: string;
}

function AccountMenu({ accountInfo, onSignOut, onSignInDifferent }: {
    accountInfo: AccountInfo;
    onSignOut?: () => void;
    onSignInDifferent?: () => void;
}) {
    const [open, setOpen] = useState(false);
    const ref = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!open) return;
        const handleClick = (e: MouseEvent) => {
            if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
        };
        document.addEventListener('mousedown', handleClick);
        return () => document.removeEventListener('mousedown', handleClick);
    }, [open]);

    return (
        <div className="relative shrink-0" ref={ref}>
            <button
                onClick={() => setOpen(p => !p)}
                className="flex items-center gap-1.5 text-brand hover:text-brand-dark transition-colors rounded-md px-1.5 py-1 hover:bg-offwhite"
                title={accountInfo.email || accountInfo.name || 'Connected account'}
            >
                <User className="w-3.5 h-3.5" />
            </button>
            {open && (
                <div className="absolute right-0 top-full mt-1 w-64 bg-white rounded-lg border border-softgrey shadow-lg z-50">
                    <div className="px-4 py-3 border-b border-softgrey">
                        <p className="text-xs text-coolslate mb-0.5">Connected account</p>
                        {accountInfo.name && <p className="text-sm font-medium text-graphite truncate">{accountInfo.name}</p>}
                        {accountInfo.email && <p className="text-xs text-coolslate truncate">{accountInfo.email}</p>}
                    </div>
                    <div className="py-1">
                        {onSignOut && (
                            <button
                                onClick={() => { setOpen(false); onSignOut(); }}
                                className="w-full flex items-center gap-2 px-4 py-2 text-sm text-graphite hover:bg-offwhite transition-colors text-left"
                            >
                                <LogOut className="w-3.5 h-3.5" />
                                Sign Out
                            </button>
                        )}
                        {onSignInDifferent && (
                            <button
                                onClick={() => { setOpen(false); onSignInDifferent(); }}
                                className="w-full flex items-center gap-2 px-4 py-2 text-sm text-graphite hover:bg-offwhite transition-colors text-left"
                            >
                                <RefreshCw className="w-3.5 h-3.5" />
                                Sign In With a Different Account
                            </button>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

export interface FolderEntry {
    id: string;
    name: string;
    subtitle?: string;
    linked?: boolean;
    linkedCount?: number;
}

interface SourceBrowserProps {
    folders: FolderEntry[];
    path: FolderEntry[];
    filesCount?: number;          // aggregate count (browse mode — folder list only)
    files?: FileRowProps[];        // file rows (files mode) — reuse FileRow's props
    loading?: boolean;
    error?: string | null;
    emptyMessage?: string;
    maxHeight?: string;

    // The caller owns `path` state.
    onNavigate: (newPath: FolderEntry[]) => void;

    title?: string;                // if set, replaces breadcrumb header with a plain title
    rootLabel?: string;            // text for the root link instead of Home icon (e.g., 'Sites')
    search?: {
        value: string;
        onChange: (value: string) => void;
        placeholder?: string;
    };

    accountInfo?: AccountInfo | null;
    onSignOut?: () => void;
    onSignInDifferent?: () => void;

    headerAction?: React.ReactNode;
}

export default function SourceBrowser({
    folders,
    path,
    filesCount,
    files,
    loading = false,
    error,
    emptyMessage = 'This folder is empty',
    maxHeight = 'max-h-64',
    onNavigate,
    title,
    rootLabel,
    search,
    accountInfo,
    onSignOut,
    onSignInDifferent,
    headerAction,
}: SourceBrowserProps) {
    if (error) {
        return (
            <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
                {error}
            </div>
        );
    }

    const isEmpty = folders.length === 0 && !filesCount && (!files || files.length === 0);

    return (
        <div className="rounded-lg border border-softgrey bg-white">
            <div className="border-b border-softgrey px-4 py-3">
                <div className="flex items-center justify-between gap-2">
                    {title ? (
                        <span className="text-sm font-medium text-graphite">{title}</span>
                    ) : (
                        <nav className="flex items-center gap-1 text-sm flex-wrap min-w-0">
                            <button
                                onClick={() => onNavigate([])}
                                className="flex items-center gap-1 text-brand hover:text-brand-dark transition-colors shrink-0"
                            >
                                {rootLabel ? rootLabel : <Home className="w-3.5 h-3.5" />}
                            </button>
                            {path.map((entry, i) => {
                                const isLast = i === path.length - 1;
                                return (
                                    <span key={entry.id} className="flex items-center gap-1">
                                        <ChevronRight className="w-3.5 h-3.5 text-coolslate/50" />
                                        {isLast ? (
                                            <span className="font-medium text-graphite">{entry.name}</span>
                                        ) : (
                                            <button
                                                onClick={() => onNavigate(path.slice(0, i + 1))}
                                                className="text-brand hover:text-brand-dark transition-colors"
                                            >
                                                {entry.name}
                                            </button>
                                        )}
                                    </span>
                                );
                            })}
                        </nav>
                    )}
                    <div className="flex items-center gap-1.5 shrink-0">
                        {headerAction}
                        {accountInfo && (
                            <AccountMenu
                                accountInfo={accountInfo}
                                onSignOut={onSignOut}
                                onSignInDifferent={onSignInDifferent}
                            />
                        )}
                    </div>
                </div>
            </div>

            {search && (
                <div className="border-b border-softgrey p-3">
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-coolslate/50" />
                        <input
                            type="text"
                            placeholder={search.placeholder}
                            value={search.value}
                            onChange={e => search.onChange(e.target.value)}
                            className="w-full pl-9 pr-3 py-2 border border-softgrey rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand"
                        />
                    </div>
                </div>
            )}

            <div className={`p-2 overflow-y-auto ${maxHeight}`}>
                {loading ? (
                    <div className="flex justify-center py-8">
                        <Loader className="w-6 h-6 animate-spin text-brand" />
                    </div>
                ) : isEmpty ? (
                    <p className="text-center text-coolslate py-6 text-sm">{emptyMessage}</p>
                ) : (
                    <div className="space-y-0.5">
                        {folders.map(folder => (
                            <FolderRow
                                key={folder.id}
                                name={folder.name}
                                subtitle={folder.subtitle}
                                linked={folder.linked}
                                linkedCount={folder.linkedCount}
                                onClick={() => onNavigate([...path, folder])}
                            />
                        ))}

                        {filesCount !== undefined && filesCount > 0 && (
                            <div className="flex items-center gap-2 px-3 py-2 text-xs text-coolslate">
                                <FileText className="w-3.5 h-3.5" />
                                {filesCount} file{filesCount !== 1 ? 's' : ''} in this folder
                            </div>
                        )}

                        {files?.map(f => (
                            <FileRow key={f.file.id} {...f} />
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
